import { supabase } from './supabase';

class AdminService {
  // ============ GLOBAL STATS ============
  async getGlobalStats() {
    try {
      // Total users
      const { count: totalUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });

      // Active businesses
      const { count: activeBusinesses } = await supabase
        .from('businesses')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      const { count: totalBusinesses } = await supabase
        .from('businesses')
        .select('*', { count: 'exact', head: true });

      // Total revenue (bu ay)
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { data: revenueData } = await supabase
        .from('revenue_logs')
        .select('amount')
        .gte('created_at', startOfMonth.toISOString());

      const totalRevenue = revenueData?.reduce((sum, r) => sum + parseFloat(r.amount), 0) || 0;

      // User growth (bu ay)
      const { count: newUsersThisMonth } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startOfMonth.toISOString());

      return {
        totalUsers,
        activeBusinesses,
        totalBusinesses,
        totalRevenue,
        newUsersThisMonth,
      };
    } catch (error) {
      console.error('Error fetching global stats:', error);
      throw error;
    }
  }

  // ============ USER GROWTH DATA ============
  async getUserGrowthData(days = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data } = await supabase
        .from('users')
        .select('created_at')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      // Günə görə qruplaşdır
      const groupedData = {};
      data?.forEach((user) => {
        const date = new Date(user.created_at).toLocaleDateString('az-AZ');
        groupedData[date] = (groupedData[date] || 0) + 1;
      });

      return Object.entries(groupedData).map(([date, count]) => ({
        date,
        users: count,
      }));
    } catch (error) {
      console.error('Error fetching user growth:', error);
      throw error;
    }
  }

  // ============ APPLICATIONS ============
  async getPendingApplicationsCount() {
    try {
      const { count, error } = await supabase
        .from('business_applications')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error getting pending applications:', error);
      return 0;
    }
  }

  async getAllApplications() {
    try {
      const { data, error } = await supabase
        .from('business_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting applications:', error);
      return [];
    }
  }

  async approveApplication(applicationId, adminId, subscriptionMonths) {
    try {
      // Get application
      const { data: app, error: appError } = await supabase
        .from('business_applications')
        .select('*')
        .eq('id', applicationId)
        .single();

      if (appError) throw appError;

      // Calculate subscription end date
      const subscriptionEndDate = new Date();
      subscriptionEndDate.setMonth(subscriptionEndDate.getMonth() + subscriptionMonths);

      // Create business row linked to the auth user who applied
      const { data: business, error: businessError } = await supabase
        .from('businesses')
        .insert({
          name: app.company_name,
          description: '',
          business_type: app.business_type || 'restaurant',
          cuisine_type: app.cuisine_type || '',
          address: app.address || '',
          city: app.city || 'Bakı',
          phone: app.phone || '',
          total_capacity: app.capacity || 50,
          owner_id: app.user_id,
          is_active: true,
          is_open: true,
          premium_tier: 1,
          subscription_start: new Date().toISOString(),
          subscription_end: subscriptionEndDate.toISOString(),
        })
        .select()
        .single();

      if (businessError) throw businessError;

      // Update application
      const { error: updateError } = await supabase
        .from('business_applications')
        .update({
          status: 'approved',
          admin_note: `Təsdiqləndi — ${subscriptionMonths} ay abunəlik`,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', applicationId);

      if (updateError) throw updateError;

      // Log action
      await this.logAction(adminId, 'approve_application', 'business', business.id, {
        application_id: applicationId,
        company_name: app.company_name,
        subscription_months: subscriptionMonths,
      });

      return { success: true, business };
    } catch (error) {
      console.error('Error approving application:', error);
      return { success: false, error: error.message };
    }
  }

  async rejectApplication(applicationId, adminId, reason) {
    try {
      const { error } = await supabase
        .from('business_applications')
        .update({
          status: 'rejected',
          rejection_reason: reason,
          reviewed_by: adminId,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', applicationId);

      if (error) throw error;

      // Log action
      await this.logAction(adminId, 'reject_application', 'business_application', applicationId, {
        reason,
      });

      return { success: true };
    } catch (error) {
      console.error('Error rejecting application:', error);
      return { success: false, error: error.message };
    }
  }

  // ============ SYSTEM LOG ============
  // system_logs cədvəlini yaratmaq üçün Supabase SQL Editor-da işlədin:
  // create table if not exists system_logs (
  //   id uuid default gen_random_uuid() primary key,
  //   admin_id uuid, action text, target_type text,
  //   target_id text, details jsonb, created_at timestamptz default now()
  // );
  async logAction(adminId, action, targetType, targetId = null, details = null) {
    try {
      await supabase.from('system_logs').insert({
        admin_id: adminId,
        action,
        target_type: targetType,
        target_id: targetId ? String(targetId) : null,
        details: details || {},
        created_at: new Date().toISOString(),
      });
    } catch (_) {
      // log fails silently — don't break main flow
    }
  }
}

const adminService = new AdminService();
export default adminService;